import React, { useState, useEffect } from 'react';
import styles from './O_CreationCapsuleViewers.module.css';
import A_RadioItem from '@/components/A_Atoms/A_RadioItem/A_RadioItem';
import A_Button from '@/components/A_Atoms/A_Button/A_Button';
import M_InviteItem from '@/components/M_Molecules/M_InviteItem/M_InviteItem';
import { useTranslation } from '@/hooks/useTranslation';
import S_InviteCoauthorsPopup from '@/components/S_Super-Organisms/S_InviteCoauthorsPopup/S_InviteCoauthorsPopup';
import { usersApi } from '@/services/usersApi';
import { capsulesApi, CapsuleInvite } from '@/services/capsulesApi';
import { useApi } from '@/hooks/useApi';

export type ViewersOption = 'all' | 'coauthors' | 'selected';

interface O_CreationCapsuleViewersProps {
  privacy?: 'public' | 'coauthors' | 'selected';
  onPrivacyChange?: (privacy: 'public' | 'coauthors' | 'selected') => void;
  viewers?: string[];
  onViewersChange?: (viewers: string[]) => void;
  onViewerEmailInvite?: (email: string) => void;
  capsuleId?: string | number;
}

function privacyToViewer(
  privacy: 'public' | 'coauthors' | 'selected'
): ViewersOption {
  if (privacy === 'public') return 'all';
  if (privacy === 'coauthors') return 'coauthors';
  return 'selected';
}

function viewerToPrivacy(
  viewer: ViewersOption
): 'public' | 'coauthors' | 'selected' {
  if (viewer === 'all') return 'public';
  if (viewer === 'coauthors') return 'coauthors';
  return 'selected';
}

export default function O_CreationCapsuleViewers({
  privacy = 'public',
  onPrivacyChange,
  viewers = [],
  onViewersChange,
  onViewerEmailInvite,
  capsuleId,
}: O_CreationCapsuleViewersProps) {
  const selectedViewer = privacyToViewer(privacy);
  const setSelectedViewer = (v: ViewersOption) =>
    onPrivacyChange?.(viewerToPrivacy(v));
  const [isInviteCoauthorsPopupOpen, setIsInviteCoauthorsPopupOpen] =
    useState(false);
  const t = useTranslation();

  const { data: viewersData, refetch: refetchViewers } = useApi(async () => {
    if (viewers.length === 0) {
      return [];
    }
    const results = await Promise.all(
      viewers.map(async userId => {
        try {
          const res = await usersApi.getUserInfo(userId);
          if (res.success && res.data) {
            const firstName = res.data.personal?.firstName || '';
            const lastName = res.data.personal?.lastName || '';
            const fullName = `${firstName} ${lastName}`.trim();
            return {
              id: userId,
              name: fullName || res.data.handle || `User ${userId}`,
              avatar: res.data.avatar || undefined,
            };
          }
          return { id: userId, name: `User ${userId}`, avatar: undefined };
        } catch (error) {
          return { id: userId, name: `User ${userId}`, avatar: undefined };
        }
      })
    );
    return results;
  }, [viewers.join(',')]);

  useEffect(() => {
    if (isInviteCoauthorsPopupOpen === false && viewers.length > 0) {
      refetchViewers();
    }
  }, [isInviteCoauthorsPopupOpen, viewers.length, refetchViewers]);

  const handleInvite = () => {
    setIsInviteCoauthorsPopupOpen(true);
  };

  const handleRemove = async (userId: string | number) => {
    const userIdString = String(userId);
    const updatedViewers = viewers.filter(id => id !== userIdString);
    onViewersChange?.(updatedViewers);
    if (capsuleId) {
      try {
        await capsulesApi.updateAllowedUsers(capsuleId, {
          allowedUserIds: updatedViewers
            .map(id => parseInt(id, 10))
            .filter(n => !isNaN(n)),
        });
        refetchViewers();
      } catch {
        // ошибка удаления viewer
      }
    }
  };

  return (
    <div className={styles.wrapper}>
      <h4 className={styles.title}>{t('capsuleCreate.viewers.title')}</h4>
      <div className={styles.content}>
        <div className={styles.radio_list}>
          <A_RadioItem
            label={t('capsuleCreate.viewers.all')}
            value='all'
            name='viewers'
            checked={selectedViewer === 'all'}
            onChange={() => setSelectedViewer('all')}
            title='Капсула будет видна всем пользователям'
          />
          <A_RadioItem
            label={t('capsuleCreate.viewers.coauthors')}
            value='coauthors'
            name='viewers'
            checked={selectedViewer === 'coauthors'}
            onChange={() => setSelectedViewer('coauthors')}
            title='Капсула будет видна только доверенным хранителям, которых вы пригласили'
          />
          <A_RadioItem
            label={t('capsuleCreate.viewers.selected')}
            value='selected'
            name='viewers'
            checked={selectedViewer === 'selected'}
            onChange={() => setSelectedViewer('selected')}
            title='Капсула будет видна только выбранному кругу лиц (не хранителям)'
          />
        </div>
        {selectedViewer === 'selected' && (
          <>
            {viewers.length > 0 && (
              <div className={styles.viewers_list}>
                {(viewersData &&
                Array.isArray(viewersData) &&
                viewersData.length > 0
                  ? viewersData
                  : viewers.map(id => ({
                      id,
                      name: `User ${id}`,
                      avatar: undefined,
                    }))
                ).map(
                  (userInfo: { id: string; name: string; avatar?: string }) => {
                    const tempInvite: CapsuleInvite = {
                      id: userInfo.id,
                      capsule: '',
                      from_user: {
                        id: '',
                        username: '',
                      },
                      to_user: {
                        id: userInfo.id,
                        username: userInfo.name,
                        first_name: userInfo.name.split(' ')[0] || '',
                        last_name:
                          userInfo.name.split(' ').slice(1).join(' ') || '',
                        avatar: userInfo.avatar || null,
                      },
                      created_at: '',
                      accepted: false,
                      is_read: false,
                    };
                    return (
                      <M_InviteItem
                        key={userInfo.id}
                        invite={tempInvite}
                        onRemove={handleRemove}
                        removeTitle={
                          t('capsuleCreate.viewers.remove') || 'Удалить'
                        }
                      />
                    );
                  }
                )}
              </div>
            )}
            <A_Button
              onClick={handleInvite}
              variant='tertiary'
              size='small'
              fontSize='text-p-4'
            >
              {t('capsuleCreate.viewers.invite')}
            </A_Button>
          </>
        )}
      </div>
      {isInviteCoauthorsPopupOpen && (
        <S_InviteCoauthorsPopup
          setIsInviteCoauthorsPopupOpen={setIsInviteCoauthorsPopupOpen}
          capsuleId={capsuleId}
          mode='viewer'
          existingAllowedUserIds={viewers
            .map(id => parseInt(id, 10))
            .filter(n => !isNaN(n))}
          onEmailInvite={onViewerEmailInvite}
          onAllowedUsersSynced={ids => onViewersChange?.(ids.map(String))}
          onUserSelect={user => {
            const userId = String(user.id);
            if (!viewers.includes(userId)) {
              onViewersChange?.([...viewers, userId]);
            }
          }}
        />
      )}
    </div>
  );
}
